// src/pages/admin/AdminCompaniesPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Stack, Typography, TextField, InputAdornment, IconButton,
  List, ListItemButton, ListItemText, Divider, Button, Chip, CircularProgress,
  Tabs, Tab, Tooltip, Grid, Snackbar, Alert
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  FolderOpen as FolderOpenIcon,
  ReceiptLong as ReceiptLongIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosClient";

/* ----------------------------- helpers ----------------------------- */
const normalizeArray = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.companies)) return payload.companies;
  if (typeof payload === "object") return Object.values(payload);
  return [];
};
const fmtDT = (s) => (s ? new Date(s).toLocaleString() : "");
const shallowDiff = (a, b) => {
  const out = {};
  Object.keys(b || {}).forEach((k) => {
    if (b[k] !== a?.[k]) out[k] = b[k];
  });
  return out;
};

/* ----------------------------- main page ----------------------------- */
export default function AdminCompaniesPage() {
  const navigate = useNavigate();

  /* Left list */
  const [companies, setCompanies] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch] = useState("");

  /* Selection + details */
  const [selectedId, setSelectedId] = useState(null);
  const [details, setDetails] = useState(null);         // original from API
  const [form, setForm] = useState(null);               // editable state
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /* Invoices */
  const [tab, setTab] = useState(0); // 0: Details, 1: Invoices
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  /* Doc count */
  const [docCount, setDocCount] = useState(null);
  const [docCountLoading, setDocCountLoading] = useState(false);

  /* Toast */
  const [toast, setToast] = useState({ open: false, kind: "success", msg: "" });

  const fireToast = (msg, kind = "success") =>
    setToast({ open: true, kind, msg });

  /* -------- fetch companies list -------- */
  const fetchCompanies = useCallback(async () => {
    setListLoading(true);
    try {
      const { data } = await api.get("/admin/companies");
      const list = normalizeArray(data).map((c) => ({
        id: c?.id ?? c?.company_id ?? c?.value ?? c?.key ?? null,
        name: c?.name ?? c?.company_name ?? c?.label ?? (c?.id ? `Company #${c.id}` : "Unnamed"),
      })).filter((c) => c.id != null);
      setCompanies(list.sort((a,b)=>String(a.name).localeCompare(String(b.name))));
      // keep selection if still present
      if (selectedId && !list.some((c) => String(c.id) === String(selectedId))) {
        setSelectedId(null);
      }
    } catch (e) {
      console.error(e);
      setCompanies([]);
      fireToast("Failed to load companies", "error");
    } finally {
      setListLoading(false);
    }
  }, [selectedId]);

  /* -------- fetch details for a company -------- */
  const fetchDetails = useCallback(async (id) => {
    if (!id) return;
    setDetailsLoading(true);
    try {
      const { data } = await api.get(`/admin/recc/companies/${id}`);
      setDetails(data || {});
      // map to editable form fields; adjust to your schema
      setForm({
        name: data?.name ?? "",
        email: data?.email ?? "",
        phone: data?.phone ?? data?.mobile ?? "",
        address: data?.address ?? "",
        vat_number: data?.vat_number ?? data?.vatNo ?? "",
        cr_number: data?.cr_number ?? data?.crNo ?? "",
        // add more mappings if available in your payload
      });
    } catch (e) {
      console.error(e);
      setDetails(null);
      setForm(null);
      fireToast("Failed to load company details", "error");
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  /* -------- fetch invoices for a company -------- */
  const fetchInvoices = useCallback(async (id) => {
    if (!id) return;
    setInvoicesLoading(true);
    try {
      const { data } = await api.get(`/admin/recc/companies/${id}/invoices`);
      setInvoices(normalizeArray(data));
    } catch (e) {
      console.error(e);
      setInvoices([]);
      fireToast("Failed to load invoices", "error");
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  /* -------- fetch document count (paginator total) -------- */
  const fetchDocCount = useCallback(async (id) => {
    if (!id) return;
    setDocCountLoading(true);
    try {
      const { data } = await api.get(`/admin/documents`, {
        params: { company_id: id, per_page: 1, page: 1 },
      });
      setDocCount(Number(data?.total ?? 0));
    } catch (e) {
      console.error(e);
      setDocCount(null);
    } finally {
      setDocCountLoading(false);
    }
  }, []);

  /* effects */
  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  useEffect(() => { 
    if (selectedId) {
      fetchDetails(selectedId);
      fetchInvoices(selectedId);
      fetchDocCount(selectedId);
    }
  }, [selectedId, fetchDetails, fetchInvoices, fetchDocCount]);

  /* derived list (client-side search) */
  const filteredCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(c =>
      String(c.name).toLowerCase().includes(q) ||
      String(c.id).toLowerCase().includes(q)
    );
  }, [companies, search]);

  /* actions */
  const onSelect = (id) => {
    setSelectedId(id);
    setTab(0);
  };

  const onFormChange = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const onSave = async () => {
    if (!selectedId || !details || !form) return;
    const base = {
      name: details?.name ?? "",
      email: details?.email ?? "",
      phone: details?.phone ?? details?.mobile ?? "",
      address: details?.address ?? "",
      vat_number: details?.vat_number ?? details?.vatNo ?? "",
      cr_number: details?.cr_number ?? details?.crNo ?? "",
    };
    const payload = shallowDiff(base, form);
    if (Object.keys(payload).length === 0) {
      fireToast("No changes to save", "info");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/recc/companies/${selectedId}`, payload);
      fireToast("Saved");
      // Refresh details to reflect server truth
      fetchDetails(selectedId);
      // Might change list name
      fetchCompanies();
    } catch (e) {
      console.error(e);
      fireToast("Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const goToDocuments = () => {
    if (!selectedId) return;
    // Adjust this path to your router/page for documents
    navigate(`/admin/company-documents?company_id=${encodeURIComponent(selectedId)}`);
  };

  /* ----------------------------- render ----------------------------- */
  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="stretch">
        {/* LEFT: Companies list */}
        <Paper sx={{ width: 360, p: 2, borderRadius: 3, flexShrink: 0 }}>
          <Stack spacing={1.5}>
            <Typography variant="h6">Companies</Typography>
            <TextField
              size="small"
              placeholder="Search by name or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={fetchCompanies} edge="end">
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Divider />
            {listLoading ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <CircularProgress size={24} />
              </Box>
            ) : filteredCompanies.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No companies.</Typography>
            ) : (
              <List dense sx={{ maxHeight: "70vh", overflowY: "auto" }}>
                {filteredCompanies.map((c) => (
                  <ListItemButton
                    key={c.id}
                    selected={String(c.id) === String(selectedId)}
                    onClick={() => onSelect(c.id)}
                  >
                    <ListItemText
                      primary={<Typography noWrap>{c.name}</Typography>}
                      secondary={`#${c.id}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Stack>
        </Paper>

        {/* RIGHT: Details / Invoices */}
        <Paper sx={{ flex: 1, p: 0, borderRadius: 3, overflow: "hidden" }}>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #e7ebf0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "rgba(1,84,142,0.06)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ mr: 1 }}>
                {details?.name ?? (selectedId ? `Company #${selectedId}` : "Select a company")}
              </Typography>
              {docCountLoading ? (
                <Chip size="small" icon={<DescriptionIcon />} label="Docs …" />
              ) : docCount != null ? (
                <Chip size="small" icon={<DescriptionIcon />} label={`${docCount} docs`} />
              ) : null}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Open documents">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FolderOpenIcon />}
                    disabled={!selectedId}
                    onClick={goToDocuments}
                  >
                    Documents
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Save changes">
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={!selectedId || !form || saving}
                    onClick={onSave}
                  >
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 1 }}>
            <Tab label="Details" />
            <Tab label={<Stack direction="row" alignItems="center" spacing={1}><ReceiptLongIcon fontSize="small" /> <span>Invoices</span></Stack>} />
          </Tabs>

          {/* Tab content */}
          <Box sx={{ p: 2 }}>
            {!selectedId ? (
              <Typography color="text.secondary">Pick a company from the list to manage.</Typography>
            ) : detailsLoading ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <CircularProgress size={24} />
              </Box>
            ) : tab === 0 ? (
              form ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Company name"
                      fullWidth
                      value={form.name}
                      onChange={(e) => onFormChange("name", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Email"
                      fullWidth
                      value={form.email}
                      onChange={(e) => onFormChange("email", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Phone"
                      fullWidth
                      value={form.phone}
                      onChange={(e) => onFormChange("phone", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="VAT Number"
                      fullWidth
                      value={form.vat_number}
                      onChange={(e) => onFormChange("vat_number", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="CR Number"
                      fullWidth
                      value={form.cr_number}
                      onChange={(e) => onFormChange("cr_number", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Address"
                      fullWidth
                      multiline
                      minRows={2}
                      value={form.address}
                      onChange={(e) => onFormChange("address", e.target.value)}
                    />
                  </Grid>

                  {/* Add any additional custom fields you support here */}

                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        onClick={() => form && setForm({
                          name: details?.name ?? "",
                          email: details?.email ?? "",
                          phone: details?.phone ?? details?.mobile ?? "",
                          address: details?.address ?? "",
                          vat_number: details?.vat_number ?? details?.vatNo ?? "",
                          cr_number: details?.cr_number ?? details?.crNo ?? "",
                        })}
                      >
                        Reset
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={saving}
                        onClick={onSave}
                      >
                        {saving ? "Saving…" : "Save changes"}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              ) : (
                <Typography color="text.secondary">No details.</Typography>
              )
            ) : (
              /* Invoices tab */
              <Box>
                {invoicesLoading ? (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : invoices.length === 0 ? (
                  <Typography color="text.secondary">No invoices.</Typography>
                ) : (
                  <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
                    <Box
                      sx={{
                        px: 2, py: 1, fontWeight: 600,
                        display: "grid",
                        gridTemplateColumns: "1.2fr 1fr 1fr 1fr",
                        borderBottom: "1px solid #e7ebf0",
                        bgcolor: "#fafbfd",
                      }}
                    >
                      <div>Invoice #</div>
                      <div>Date</div>
                      <div>Status</div>
                      <div>Amount</div>
                    </Box>
                    {invoices.map((inv) => (
                      <Box
                        key={inv.id ?? inv.invoiceNo ?? Math.random()}
                        sx={{
                          px: 2, py: 1,
                          display: "grid",
                          gridTemplateColumns: "1.2fr 1fr 1fr 1fr",
                          borderBottom: "1px solid #f0f2f5",
                        }}
                      >
                        <Typography noWrap>{inv.invoiceNo ?? inv.id ?? "-"}</Typography>
                        <Typography noWrap>{fmtDT(inv.date ?? inv.created_at)}</Typography>
                        <Typography noWrap>{String(inv.status ?? inv.state ?? "-")}</Typography>
                        <Typography noWrap>{inv.amount ?? inv.total ?? "-"}</Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Stack>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.kind}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
